import { Classes, FormGroup, HTMLSelect, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { Dialog } from 'src/ui/components/Dialog';
import { FormAction, FormActions } from 'src/ui/components/FormActions';
import { Loading } from 'src/ui/components/Loading';
import {
  CLOUD_REPORT_CLEANUP_ACTION_SELECT,
  CLOUD_REPORT_CLEANUP_DIALOG,
  CLOUD_REPORT_CLEANUP_EMPTY,
  CLOUD_REPORT_CLEANUP_MEMBERS_WITHOUT,
  CLOUD_REPORT_CLEANUP_NOT_MOVED,
  CLOUD_REPORT_CLEANUP_ORPHAN_LIST,
  CLOUD_REPORT_CLEANUP_ROLE_SELECT,
} from 'src/ui/constants/testIds';
import { makeSubmitAction } from 'src/ui/containers/dialogs/makeSubmitAction';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  OrphanedConnectionAction,
  useGetOrgConnectionCleanupPreviewQuery,
  useResolveOrphanedConnectionsMutation,
} from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';

const dialogCopy = copy.components.cleanupOrgConnectionDialog;

const MEMBER_ROLE = 'MEMBER';
const ADMIN_ROLE = 'ADMIN';
const ROLE_OPTIONS = [
  { label: dialogCopy.roleMember, value: MEMBER_ROLE },
  { label: dialogCopy.roleAdmin, value: ADMIN_ROLE },
];
const ACTION_OPTIONS = [
  { label: dialogCopy.actionLeave, value: OrphanedConnectionAction.Leave },
  { label: dialogCopy.actionMigrate, value: OrphanedConnectionAction.Migrate },
  { label: dialogCopy.actionRemove, value: OrphanedConnectionAction.Remove },
];

type CleanupOrgConnectionDialogProps = {
  cloudId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const CleanupOrgConnectionDialog: React.VFC<CleanupOrgConnectionDialogProps> = ({
  cloudId,
  isOpen,
  onClose,
}) => {
  const isMounted = useIsMounted();
  const onMutate = useOnMutate();
  const onError = useOnError();
  const queryClient = useQueryClient();

  const [actions, setActions] = React.useState<
    Record<string, OrphanedConnectionAction>
  >({});
  const [roles, setRoles] = React.useState<Record<string, string>>({});

  const { data, isLoading } = useGetOrgConnectionCleanupPreviewQuery(
    graphQLClient,
    { cloud_id: cloudId },
    { enabled: isOpen }
  );
  const preview = data?.getOrgConnectionCleanupPreview;
  const orphaned = React.useMemo(
    () =>
      (preview?.orphaned ?? []).flatMap((entry) =>
        entry?.connection_id ? [entry] : []
      ),
    [preview]
  );

  // Default every orphan to LEAVE (non-destructive) and MEMBER role.
  React.useEffect(() => {
    const ids = orphaned.map((entry) => entry.connection_id as string);
    setActions((prev) =>
      Object.fromEntries(
        ids.map((id) => [
          id,
          id in prev ? prev[id] : OrphanedConnectionAction.Leave,
        ])
      )
    );
    setRoles((prev) =>
      Object.fromEntries(
        ids.map((id) => [id, id in prev ? prev[id] : MEMBER_ROLE])
      )
    );
  }, [orphaned]);

  const handleClose = () => {
    setActions({});
    setRoles({});
    onClose();
  };

  const {
    isLoading: isApplying,
    mutate: resolveOrphaned,
  } = useResolveOrphanedConnectionsMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: async (response) => {
      if (!response.resolveOrphanedConnections?.success) {
        return;
      }
      /* istanbul ignore if */
      if (!isMounted.current) {
        return;
      }
      await queryClient.refetchQueries(['getCloudReport']);
      /* istanbul ignore if */
      if (!isMounted.current) {
        return;
      }
      handleClose();
    },
  });

  const isOrgConnection = preview?.is_org_connection === true;
  const isSubmitDisabled = isLoading || isApplying || !isOrgConnection;

  const handleSubmit = (evt?: React.FormEvent) => {
    evt?.preventDefault();
    if (isSubmitDisabled || !preview?.connection_id) {
      return;
    }

    const resolvedActions = orphaned.map((entry) => {
      const connectionId = entry.connection_id as string;
      const action = actions[connectionId] ?? OrphanedConnectionAction.Leave;
      return {
        connection_id: connectionId,
        action,
        organization_role:
          action === OrphanedConnectionAction.Migrate
            ? roles[connectionId] ?? MEMBER_ROLE
            : undefined,
      };
    });

    resolveOrphaned({
      input: {
        connection_id: preview.connection_id,
        actions: resolvedActions,
      },
    });
  };

  const dialogActions: FormAction[] = [
    { text: dialogCopy.cancel, onClick: handleClose },
    makeSubmitAction(dialogCopy.submit, isApplying),
  ];

  let body: React.ReactNode;
  if (isLoading) {
    body = <Loading />;
  } else if (!isOrgConnection) {
    body = (
      <p data-testid={CLOUD_REPORT_CLEANUP_NOT_MOVED}>{dialogCopy.notMoved}</p>
    );
  } else {
    const membersWithout = (preview?.org_members_without_access ?? []).filter(
      Boolean
    ) as string[];

    body = (
      <form className="cloud-report__move-form" onSubmit={handleSubmit}>
        <p className={Classes.TEXT_MUTED}>
          {dialogCopy.org(preview?.org_name ?? '')}
        </p>
        <p className={Classes.TEXT_MUTED}>
          {dialogCopy.endpoint(preview?.endpoint ?? '')}
        </p>

        <div className="cloud-report__move-content">
          <FormGroup label={dialogCopy.orphanedHeading}>
            {!orphaned.length ? (
              <p
                className={Classes.TEXT_MUTED}
                data-testid={CLOUD_REPORT_CLEANUP_EMPTY}
              >
                {dialogCopy.empty}
              </p>
            ) : (
              <div
                className="cloud-report__move-list"
                data-testid={CLOUD_REPORT_CLEANUP_ORPHAN_LIST}
              >
                {orphaned.map((entry, idx) => {
                  const connectionId = entry.connection_id as string;
                  const action =
                    actions[connectionId] ?? OrphanedConnectionAction.Leave;
                  return (
                    <div
                      className="cloud-report__cleanup-access-item"
                      key={connectionId}
                    >
                      <span className="cloud-report__cleanup-access-email">
                        {entry.owner_email}{' '}
                        {entry.is_org_member ? (
                          <Tag minimal>{dialogCopy.isMember}</Tag>
                        ) : null}{' '}
                        {entry.has_org_access ? (
                          <Tag intent="success" minimal>
                            {dialogCopy.hasOrgAccess}
                          </Tag>
                        ) : null}
                      </span>
                      <div className="cloud-report__cleanup-access-controls">
                        <HTMLSelect
                          data-testid={CLOUD_REPORT_CLEANUP_ACTION_SELECT(
                            connectionId,
                            idx
                          )}
                          disabled={isApplying}
                          onChange={(sel) => {
                            const { value } = sel.currentTarget;
                            setActions((prev) => ({
                              ...prev,
                              [connectionId]: value as OrphanedConnectionAction,
                            }));
                          }}
                          options={ACTION_OPTIONS}
                          value={action}
                        />
                        {action === OrphanedConnectionAction.Migrate ? (
                          <HTMLSelect
                            data-testid={CLOUD_REPORT_CLEANUP_ROLE_SELECT(
                              connectionId,
                              idx
                            )}
                            disabled={isApplying}
                            onChange={(sel) => {
                              const { value } = sel.currentTarget;
                              setRoles((prev) => ({
                                ...prev,
                                [connectionId]: value,
                              }));
                            }}
                            options={ROLE_OPTIONS}
                            value={roles[connectionId] ?? MEMBER_ROLE}
                          />
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </FormGroup>

          <FormGroup label={dialogCopy.membersHeading}>
            {!membersWithout.length ? (
              <p className={Classes.TEXT_MUTED}>{dialogCopy.membersNone}</p>
            ) : (
              <div
                className="cloud-report__move-list"
                data-testid={CLOUD_REPORT_CLEANUP_MEMBERS_WITHOUT}
              >
                {membersWithout.map((email) => (
                  <div className="cloud-report__move-access-item" key={email}>
                    {email}
                  </div>
                ))}
              </div>
            )}
          </FormGroup>
        </div>

        <input hidden type="submit" />
      </form>
    );
  }

  return (
    <Dialog
      canEscapeKeyClose={!isApplying}
      canOutsideClickClose={!isApplying}
      className="cloud-report__move-dialog"
      icon={IconNames.CLEAN}
      isOpen={isOpen}
      onClose={handleClose}
      title={dialogCopy.title}
    >
      <div
        className={Classes.DIALOG_BODY}
        data-testid={CLOUD_REPORT_CLEANUP_DIALOG}
      >
        {body}
      </div>
      <FormActions
        actions={dialogActions}
        handleSubmit={handleSubmit}
        isSubmitDisabled={isSubmitDisabled}
      />
    </Dialog>
  );
};
